import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import {
  MONTHS_LONG,
  MONTHS_SHORT,
  WEEKDAYS_SHORT,
  addDays,
  addMonths,
  clampDateToMonth,
  dayAriaLabel,
  endOfWeek,
  formatDateTimeValue,
  formatDateValue,
  formatMonthValue,
  formatTemporalDisplay,
  formatTimeValue,
  from12Hour,
  isDateInRange,
  isDateTimeInRange,
  isMonthInRange,
  isTimeInRange,
  monthGrid,
  nextFullHour,
  parseDateTimeValue,
  parseDateValue,
  parseMonthValue,
  parseTimeValue,
  sameDate,
  startOfWeek,
  to12Hour,
  todayParts,
  type DateParts,
  type DateTimeParts,
  type MonthParts,
  type TimeParts,
} from "../lib/temporal";

const triggerCls =
  "relative flex w-full min-w-0 max-w-full items-center rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-11 text-left text-base text-ink focus:border-brand-500";

type PickerType = "date" | "datetime-local" | "time" | "month" | "week";

type Props = InputHTMLAttributes<HTMLInputElement>;

function emitChange(
  onChange: Props["onChange"],
  value: string
): ChangeEvent<HTMLInputElement> {
  const event = {
    target: { value },
    currentTarget: { value },
  } as ChangeEvent<HTMLInputElement>;
  onChange?.(event);
  return event;
}

function CalendarGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M4 9h16M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ClockGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
      <path d="M12 8v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
      <path
        d={dir === "left" ? "M15 5 8 12l7 7" : "m9 5 7 7-7 7"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useDesktop(): boolean {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 640px)").matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const onChange = () => setDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

function NavIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-soft hover:bg-slate-100"
    >
      {children}
    </button>
  );
}

function TimeRow({
  hour,
  minute,
  onChange,
  min,
  max,
}: {
  hour: number;
  minute: number;
  onChange: (next: TimeParts) => void;
  min?: string;
  max?: string;
}) {
  const { hour12, isPm } = to12Hour(hour);
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  function apply(nextHour12: number, nextMinute: number, nextPm: boolean) {
    const next = from12Hour(nextHour12, nextMinute, nextPm);
    if (!isTimeInRange(next, min, max)) return;
    onChange(next);
  }

  const selectCls =
    "h-11 rounded-lg border border-slate-300 bg-white px-2 text-base text-ink focus:border-brand-500 focus:outline-none";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Hora"
        className={selectCls}
        value={hour12}
        onChange={(e) => apply(Number(e.target.value), minute, isPm)}
      >
        {hours.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-ink-muted">:</span>
      <select
        aria-label="Minutos"
        className={selectCls}
        value={minute}
        onChange={(e) => apply(hour12, Number(e.target.value), isPm)}
      >
        {minutes.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, "0")}
          </option>
        ))}
      </select>
      <div className="flex rounded-lg bg-slate-100 p-1">
        <button
          type="button"
          aria-pressed={!isPm}
          className={cn(
            "h-9 rounded-md px-3 text-sm font-semibold",
            !isPm ? "bg-brand-600 text-white" : "text-ink-muted"
          )}
          onClick={() => apply(hour12, minute, false)}
        >
          a. m.
        </button>
        <button
          type="button"
          aria-pressed={isPm}
          className={cn(
            "h-9 rounded-md px-3 text-sm font-semibold",
            isPm ? "bg-brand-600 text-white" : "text-ink-muted"
          )}
          onClick={() => apply(hour12, minute, true)}
        >
          p. m.
        </button>
      </div>
    </div>
  );
}

function Calendar({
  view,
  selected,
  cursor,
  min,
  max,
  onCursor,
  onSelectDay,
  onView,
}: {
  view: MonthParts;
  selected: DateParts | null;
  cursor: DateParts;
  min?: string;
  max?: string;
  onCursor: (next: DateParts) => void;
  onSelectDay: (day: DateParts) => void;
  onView: (next: MonthParts) => void;
}) {
  const grid = monthGrid(view.year, view.month);
  const today = todayParts();
  const title = `${MONTHS_LONG[view.month - 1]} ${view.year}`;

  function move(next: DateParts) {
    onCursor(next);
    if (next.month !== view.month || next.year !== view.year) {
      onView({ year: next.year, month: next.month });
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    let next: DateParts | null = null;
    if (e.key === "ArrowLeft") next = addDays(cursor, -1);
    else if (e.key === "ArrowRight") next = addDays(cursor, 1);
    else if (e.key === "ArrowUp") next = addDays(cursor, -7);
    else if (e.key === "ArrowDown") next = addDays(cursor, 7);
    else if (e.key === "PageUp") {
      const m = addMonths(view, e.shiftKey ? -12 : -1);
      next = clampDateToMonth(m.year, m.month, cursor.day);
    } else if (e.key === "PageDown") {
      const m = addMonths(view, e.shiftKey ? 12 : 1);
      next = clampDateToMonth(m.year, m.month, cursor.day);
    } else if (e.key === "Home") next = startOfWeek(cursor);
    else if (e.key === "End") next = endOfWeek(cursor);
    else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (isDateInRange(cursor, min, max, "date")) {
        onSelectDay(cursor);
      }
      return;
    } else {
      return;
    }
    e.preventDefault();
    move(next);
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <NavIconButton label="Mes anterior" onClick={() => onView(addMonths(view, -1))}>
          <Chevron dir="left" />
        </NavIconButton>
        <p className="text-sm font-semibold text-ink">{title}</p>
        <NavIconButton label="Mes siguiente" onClick={() => onView(addMonths(view, 1))}>
          <Chevron dir="right" />
        </NavIconButton>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-ink-muted">
        {WEEKDAYS_SHORT.map((d, i) => (
          <div key={`${d}-${i}`} className="py-1">
            {d}
          </div>
        ))}
      </div>
      <div
        role="grid"
        aria-label={title}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="grid grid-cols-7 gap-1 outline-none"
      >
        {grid.map((cell) => {
          const enabled = isDateInRange(cell, min, max, "date");
          const isSelected = selected ? sameDate(cell, selected) : false;
          const isToday = sameDate(cell, today);
          const isCursor = sameDate(cell, cursor);
          return (
            <button
              key={`${cell.year}-${cell.month}-${cell.day}`}
              type="button"
              role="gridcell"
              aria-label={dayAriaLabel(cell)}
              aria-selected={isSelected}
              disabled={!enabled}
              tabIndex={-1}
              className={cn(
                "flex h-9 w-full items-center justify-center rounded-lg text-sm",
                !cell.inMonth && "text-slate-300",
                cell.inMonth && !isSelected && "text-ink",
                isToday && !isSelected && "ring-1 ring-brand-500",
                isSelected && "bg-brand-600 font-semibold text-white",
                isCursor && !isSelected && "bg-slate-100",
                !enabled && "cursor-not-allowed opacity-40"
              )}
              onClick={() => enabled && onSelectDay(cell)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DatePicker({
  className,
  type = "date",
  value,
  defaultValue,
  onChange,
  disabled,
  placeholder,
  id,
  required,
  name,
  min,
  max,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: Props) {
  const pickerType = (type as PickerType) || "date";
  const controlled = value !== undefined;
  const [uncontrolled, setUncontrolled] = useState(String(defaultValue ?? ""));
  const raw = controlled ? String(value ?? "") : uncontrolled;
  const display = formatTemporalDisplay(pickerType, raw);
  const empty = !display;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const desktop = useDesktop();
  const titleId = useId().replace(/:/g, "");

  const [view, setView] = useState<MonthParts>(() => todayParts());
  const [cursor, setCursor] = useState<DateParts>(() => todayParts());
  const [draftTime, setDraftTime] = useState<TimeParts>(() => {
    const next = nextFullHour();
    return { hour: next.hour, minute: next.minute };
  });
  const [draftDate, setDraftDate] = useState<DateParts | null>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });

  const commit = useCallback(
    (next: string, close = false) => {
      if (!controlled) setUncontrolled(next);
      emitChange(onChangeRef.current, next);
      if (close) setOpen(false);
    },
    [controlled]
  );

  function syncDraftFromValue(current: string) {
    if (pickerType === "time") {
      const time = parseTimeValue(current);
      if (time) setDraftTime(time);
      return;
    }
    if (pickerType === "month") {
      const month = parseMonthValue(current) ?? toMonthParts(todayParts());
      setView(month);
      return;
    }
    if (pickerType === "datetime-local") {
      const parts = parseDateTimeValue(current);
      if (parts) {
        setDraftDate(parts);
        setDraftTime({ hour: parts.hour, minute: parts.minute });
        setView({ year: parts.year, month: parts.month });
        setCursor(parts);
        return;
      }
      const next = nextFullHour();
      setDraftDate(null);
      setDraftTime({ hour: next.hour, minute: next.minute });
      setView({ year: next.year, month: next.month });
      setCursor(next);
      return;
    }
    const date = parseDateValue(current) ?? todayParts();
    setDraftDate(parseDateValue(current));
    setView({ year: date.year, month: date.month });
    setCursor(date);
  }

  function openPicker() {
    if (disabled) return;
    syncDraftFromValue(raw);
    setOpen(true);
  }

  function closePicker() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      closePicker();
    };
    document.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !desktop) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      closePicker();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, desktop]);

  useLayoutEffect(() => {
    if (!open || !desktop) return;
    const place = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const panel = panelRef.current?.getBoundingClientRect();
      if (!trigger) return;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const panelW = Math.min(20.5 * 16, vw - margin * 2);
      const panelH = panel?.height ?? 380;
      let top = trigger.bottom + margin;
      if (top + panelH > vh - margin) top = trigger.top - margin - panelH;
      if (top < margin) top = margin;
      let left = trigger.left;
      if (left + panelW > vw - margin) left = vw - margin - panelW;
      if (left < margin) left = margin;
      setPos({ top, left, width: panelW });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, desktop, pickerType, draftTime, view]);

  function selectedDate(): DateParts | null {
    if (draftDate) return draftDate;
    if (pickerType === "datetime-local") return parseDateTimeValue(raw);
    return parseDateValue(raw);
  }

  function onSelectDay(day: DateParts) {
    if (pickerType === "datetime-local") {
      setDraftDate(day);
      setCursor(day);
      const next: DateTimeParts = { ...day, ...draftTime };
      if (!isDateTimeInRange(next, min as string | undefined, max as string | undefined)) return;
      commit(formatDateTimeValue(next));
      return;
    }
    commit(formatDateValue(day), true);
  }

  function onTimeChange(next: TimeParts) {
    setDraftTime(next);
    if (pickerType === "time") {
      commit(formatTimeValue(next));
      return;
    }
    const day = draftDate ?? parseDateTimeValue(raw) ?? todayParts();
    setDraftDate(day);
    commit(formatDateTimeValue({ ...day, ...next }));
  }

  function onSelectMonth(month: MonthParts) {
    commit(formatMonthValue(month), true);
  }

  function onToday() {
    const today = todayParts();
    if (!isDateInRange(today, min as string | undefined, max as string | undefined, "date")) return;
    if (pickerType === "datetime-local") {
      const next = { ...today, ...draftTime };
      commit(formatDateTimeValue(next));
      setDraftDate(today);
      setCursor(today);
      setView({ year: today.year, month: today.month });
      return;
    }
    commit(formatDateValue(today), true);
  }

  function onClear() {
    commit("", true);
  }

  function onDone() {
    if (pickerType === "time" && !raw) commit(formatTimeValue(draftTime));
    if (pickerType === "datetime-local" && !raw) {
      const day = draftDate ?? todayParts();
      commit(formatDateTimeValue({ ...day, ...draftTime }));
    }
    closePicker();
  }

  const showCalendar = pickerType === "date" || pickerType === "datetime-local" || pickerType === "week";
  const showTime = pickerType === "time" || pickerType === "datetime-local";
  const showMonth = pickerType === "month";
  const showClear = !required;
  const selectedMonth = parseMonthValue(raw);

  const panel = open ? (
    <div
      className={
        desktop
          ? "fixed z-[70]"
          : "fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
      }
      style={desktop ? { top: pos.top, left: pos.left, width: pos.width } : undefined}
    >
      {!desktop ? (
        <button
          type="button"
          className="absolute inset-0 bg-ink/40"
          aria-label="Cerrar"
          onClick={closePicker}
        />
      ) : null}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "relative z-10 bg-white shadow-card ring-1 ring-slate-900/5",
          desktop
            ? "rounded-xl p-3"
            : "w-full rounded-t-2xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-w-lg sm:rounded-2xl"
        )}
      >
        <p id={titleId} className="sr-only">
          {pickerType === "time"
            ? "Elegir hora"
            : pickerType === "month"
              ? "Elegir mes"
              : pickerType === "datetime-local"
                ? "Elegir fecha y hora"
                : "Elegir fecha"}
        </p>
        <div className="space-y-3">
          {showCalendar ? (
            <Calendar
              view={view}
              selected={selectedDate()}
              cursor={cursor}
              min={min as string | undefined}
              max={max as string | undefined}
              onCursor={setCursor}
              onSelectDay={onSelectDay}
              onView={setView}
            />
          ) : null}
          {showMonth ? (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <NavIconButton
                  label="Año anterior"
                  onClick={() => setView((v) => ({ year: v.year - 1, month: v.month }))}
                >
                  <Chevron dir="left" />
                </NavIconButton>
                <p className="text-sm font-semibold text-ink">{view.year}</p>
                <NavIconButton
                  label="Año siguiente"
                  onClick={() => setView((v) => ({ year: v.year + 1, month: v.month }))}
                >
                  <Chevron dir="right" />
                </NavIconButton>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {MONTHS_SHORT.map((label, idx) => {
                  const month: MonthParts = { year: view.year, month: idx + 1 };
                  const enabled = isMonthInRange(month, min as string | undefined, max as string | undefined);
                  const isSelected =
                    selectedMonth?.year === month.year && selectedMonth?.month === month.month;
                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={!enabled}
                      className={cn(
                        "h-11 rounded-lg text-sm font-medium capitalize",
                        isSelected ? "bg-brand-600 text-white" : "bg-slate-50 text-ink hover:bg-slate-100",
                        !enabled && "cursor-not-allowed opacity-40"
                      )}
                      onClick={() => enabled && onSelectMonth(month)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {showTime ? (
            <TimeRow
              hour={draftTime.hour}
              minute={draftTime.minute}
              onChange={onTimeChange}
              min={pickerType === "time" ? (min as string | undefined) : undefined}
              max={pickerType === "time" ? (max as string | undefined) : undefined}
            />
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              {showCalendar ? (
                <button
                  type="button"
                  className="h-9 rounded-lg px-3 text-sm font-semibold text-brand-700 hover:bg-brand-50"
                  onClick={onToday}
                >
                  Hoy
                </button>
              ) : null}
              {showClear ? (
                <button
                  type="button"
                  className="h-9 rounded-lg px-3 text-sm font-semibold text-ink-muted hover:bg-slate-100"
                  onClick={onClear}
                >
                  Borrar
                </button>
              ) : null}
            </div>
            {showTime ? (
              <button
                type="button"
                className="h-9 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white"
                onClick={onDone}
              >
                Listo
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className={cn("relative w-full min-w-0 max-w-full", disabled && "opacity-60", className)}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-required={required || undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(triggerCls, empty && "text-slate-400", disabled && "cursor-not-allowed")}
        onClick={() => (open ? closePicker() : openPicker())}
      >
        <span className="block min-w-0 flex-1 truncate">{display || placeholder || "\u00a0"}</span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-ink-muted">
          {pickerType === "time" ? <ClockGlyph /> : <CalendarGlyph />}
        </span>
      </button>
      <input
        type="text"
        name={name}
        value={raw}
        required={required}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute h-px w-px opacity-0"
        onChange={() => undefined}
        onInvalid={(e) => {
          e.preventDefault();
          triggerRef.current?.focus();
          if (!open) openPicker();
        }}
      />
      {open && typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </div>
  );
}

function toMonthParts(parts: DateParts): MonthParts {
  return { year: parts.year, month: parts.month };
}
